/**
 * Onboarding option catalogue (issue #362).
 *
 * The answer lists every role's onboarding form offers, and the limits on the
 * free-text fields beside them. The frontend imports these at runtime. The
 * backend cannot import a runtime value from `@shared/types`, so
 * `apps/backend/src/modules/users/onboarding-fields.ts` keeps its own copy and
 * links each one back here with `satisfies`, which fails type-check when the
 * two drift.
 *
 * Values are snake_case slugs, rendered through i18n
 * (`onboardingOptions.<group>.<value>`). The two exceptions are
 * `DESIGNER_SERVICES` and `DESIGN_STYLES`: those columns already hold English
 * display strings that the marketplace renders as stored, so their values are
 * the English labels.
 */

/** "What brings you to Finishd?" — homeowner, multi-select. */
export const HOMEOWNER_GOALS = [
	"renovate_home",
	"remodel_room",
	"decorate_furnish",
	"build_new_home",
	"improve_outdoor",
	"find_professional",
	"get_inspiration",
	"just_exploring",
] as const;
export type HomeownerGoal = (typeof HOMEOWNER_GOALS)[number];

/**
 * Property types a homeowner can pick. A subset of the `property_type`
 * database enum, which listings share: `plot` is listings-only.
 */
export const HOMEOWNER_PROPERTY_TYPES = [
	"house",
	"apartment",
	"builder_floor",
	"villa",
	"other",
] as const;
export type HomeownerPropertyType = (typeof HOMEOWNER_PROPERTY_TYPES)[number];

/** Where the homeowner is with the property. */
export const PROPERTY_STATUSES = [
	"owned_moving_in",
	"owned_renovating",
	"under_construction",
	"buying_soon",
	"renting",
] as const;
export type PropertyStatus = (typeof PROPERTY_STATUSES)[number];

/** Designer services, stored as these English strings. */
export const DESIGNER_SERVICES = [
	"Space Planning",
	"Interior Design",
	"Interior Decoration",
	"3D Visualization / Rendering",
	"Material & Finish Selection",
	"Furniture Selection",
	"Lighting Design",
	"Kitchen & Bath Design",
	"Custom Furniture / Millwork",
	"Project Management",
	"Styling / Home Staging",
	"Consultation",
] as const;

/** Suggested design styles, stored as these English strings. Designers may add their own. */
export const DESIGN_STYLES = [
	"Contemporary",
	"Modern",
	"Traditional",
	"Minimalist",
	"Industrial",
	"Scandinavian",
	"Bohemian",
	"Mid-Century Modern",
	"Art Deco",
] as const;

/** "What type of projects do you specialize in?" — designer. */
export const DESIGNER_PROJECT_TYPES = [
	"renovation",
	"new_construction",
	"full_home",
	"single_room",
	"apartment_flat",
	"villa_house",
	"luxury_residential",
	"rental_investment",
	"other",
] as const;
export type DesignerProjectType = (typeof DESIGNER_PROJECT_TYPES)[number];

/** Project types a contractor takes on. */
export const CONTRACTOR_PROJECT_TYPES = [
	"new_construction",
	"home_renovation",
	"remodeling",
	"kitchen",
	"bathroom",
	"interior",
	"exterior",
	"home_additions",
	"repairs_maintenance",
	"outdoor_landscaping",
	"commercial",
	"other",
] as const;
export type ContractorProjectType = (typeof CONTRACTOR_PROJECT_TYPES)[number];

/** "What types of properties do you specialize in?" — realtor. */
export const REALTOR_PROPERTY_TYPES = [
	"residential",
	"commercial",
	"industrial",
	"land",
	"independent_house",
	"apartment_flat",
	"builder_floor",
	"villa",
	"multi_family",
	"luxury",
	"new_construction",
	"investment",
] as const;
export type RealtorPropertyType = (typeof REALTOR_PROPERTY_TYPES)[number];

/** "Who do you primarily work with?" — realtor, optional. */
export const REALTOR_CLIENT_TYPES = [
	"buyers",
	"sellers",
	"first_time_buyers",
	"investors",
	"luxury_clients",
	"relocating_clients",
	"families",
	"builders_developers",
	"land_buyers",
	"commercial_clients",
] as const;
export type RealtorClientType = (typeof REALTOR_CLIENT_TYPES)[number];

/** The 28 states and 8 union territories of India. */
export const INDIAN_STATES = [
	"Andhra Pradesh",
	"Arunachal Pradesh",
	"Assam",
	"Bihar",
	"Chhattisgarh",
	"Goa",
	"Gujarat",
	"Haryana",
	"Himachal Pradesh",
	"Jharkhand",
	"Karnataka",
	"Kerala",
	"Madhya Pradesh",
	"Maharashtra",
	"Manipur",
	"Meghalaya",
	"Mizoram",
	"Nagaland",
	"Odisha",
	"Punjab",
	"Rajasthan",
	"Sikkim",
	"Tamil Nadu",
	"Telangana",
	"Tripura",
	"Uttar Pradesh",
	"Uttarakhand",
	"West Bengal",
	"Andaman and Nicobar Islands",
	"Chandigarh",
	"Dadra and Nagar Haveli and Daman and Diu",
	"Delhi",
	"Jammu and Kashmir",
	"Ladakh",
	"Lakshadweep",
	"Puducherry",
] as const;

/** An Indian PIN code: six digits, not starting with 0. */
export const PINCODE_PATTERN = /^[1-9][0-9]{5}$/;

export function isPincode(value: string): boolean {
	return PINCODE_PATTERN.test(value);
}

/** Longest answer the "Other" text box takes. */
export const OTHER_TEXT_MAX_LENGTH = 80;
/** Longest designer bio (Decision 9 on #362). */
export const DESIGNER_BIO_MAX_LENGTH = 300;
/** Longest custom design style a designer can add. */
export const CUSTOM_STYLE_MAX_LENGTH = 50;
/** The contractor service radius, in km. */
export const SERVICE_RADIUS_KM_MIN = 1;
export const SERVICE_RADIUS_KM_MAX = 500;
